import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { Observable, of, ReplaySubject, throwError } from "rxjs";
import { catchError, map, switchMap, tap, share, finalize } from "rxjs/operators";

import { User } from "@api/defs/User";
import {
  ImiauthHelloFormService,
  UserCurrentFormService,
  // UserPartialUpdateFormService,
} from "@api/form-service";
import { IMIAuthorization } from "@api/model";
import { environment } from "environments/environment";
import { IdpConfig, RuntimeConfigService } from "../../runtime-config.service";

@Injectable({
  providedIn: "platform",
})
export class AuthService {
  private whoamiFS = inject(ImiauthHelloFormService);
  private router = inject(Router);
  private currentUserFS = inject(UserCurrentFormService);
  // private patchUserFS = inject(UserPartialUpdateFormService);
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private config = inject(RuntimeConfigService);
  public whoami$: Observable<IMIAuthorization>;
  public currentUser$: Observable<User>;
  public impersonatedRole = null;
  expiryTime;
  whoamiSubject = new ReplaySubject<IMIAuthorization>(1);
  currentUserSubject = new ReplaySubject<User>(1);
  private tokenRequest$: Observable<any> | null = null;
  private refreshTokenRequest$: Observable<any> | null = null;
  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);
  constructor() {
    this.whoami$ = this.whoamiSubject.asObservable();
    this.currentUser$ = this.currentUserSubject.asObservable();
  }
  getUser(): Observable<User> {
    return this.whoamiFS.submit().pipe(
      tap((value: IMIAuthorization) => {
        this.whoamiSubject.next(value);
      }),
      catchError(() =>
        of(
          this.snackBar.open("S'ha producit un error amb user", "Tancar", {
            panelClass: "error",
            duration: 5000,
          })
        )
      ),
      switchMap(() =>
        this.currentUserFS.submit({}, false).pipe(
          tap((user: User) => {
            this.currentUserSubject.next(user);
          })
        )
      )
    );
  }

  // patchUser(lopd_accepted: boolean) {
  //   return this.currentUserFS.submit({}, false).pipe(
  //     switchMap((user) => {
  //       return this.patchUserFS.submit({ id: user.id, data: { lopd_accepted: lopd_accepted } });
  //     })
  //   );
  // }

  setRole(role: string): void {
    if (role) {
      this.impersonatedRole = [role];
    } else {
      this.impersonatedRole = null;
    }
  }

  isAllowed(services: [any] | object): Observable<boolean> {
    let serv = [];
    if (Array.isArray(services)) {
      serv = services;
    } else {
      serv.push(services);
    }

    if (serv.length > 0) {
      return this.whoami$.pipe(
        map((val) => {
          let hit = true;
          serv.forEach((service) => {
            let service_hit = false;
            // Rol Impersonation
            let roles = val.acces;
            if (this.impersonatedRole) {
              roles = this.impersonatedRole;
            }
            if (roles) {
              roles.forEach((rol) => {
                if (
                  service["xImiRoles"] &&
                  Object.keys(service["xImiRoles"]).includes(rol)
                ) {
                  service_hit = true;
                }
              });
            }
            if (!service_hit) {
              hit = false;
            }
          });
          return hit;
        })
      );
    } else {
      return of(true);
    }
  }
  setAccessToken(token: string): void {
    localStorage.setItem("accessToken", token);
  }
  getAccessToken(): string {
    return localStorage.getItem("accessToken") &&
      localStorage.getItem("accessToken") !== "undefined" &&
      localStorage.getItem("accessToken") !== "null"
      ? localStorage.getItem("accessToken")
      : undefined;
  }
  // Used for the PKCE code verifier and the OAuth state, so it must come from a CSPRNG, not Math.random().
  randomAlphaNumeric(length: number): string {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    return Array.from(crypto.getRandomValues(new Uint8Array(length)), (b) => chars[b % chars.length]).join("");
  }

  async sha256(plain: string): Promise<ArrayBuffer> {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return await crypto.subtle.digest('SHA-256', data);
  }

  base64urlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCodePoint(bytes[i]);
    }
    return btoa(binary)
      .replaceAll('+', '-')
      .replaceAll('/', '_')
      .replaceAll('=', '');
  }

  async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const hashed = await this.sha256(codeVerifier);
    return this.base64urlEncode(hashed);
  }

  redirectIDP(): void {
    if (environment) {
      if (
        localStorage.getItem("codeVerifier") &&
        localStorage.getItem("codeVerifierTimestamp")
      ) {
        if (
          Number(localStorage.getItem("codeVerifierTimestamp")) < Date.now()
        ) {
          localStorage.removeItem("codeVerifier");
          localStorage.removeItem("codeVerifierTimestamp");
        }
      }
      if (!localStorage.getItem("codeVerifier")) {
        const codeVerifier = this.randomAlphaNumeric(128);
        localStorage.setItem("codeVerifier", codeVerifier);
        localStorage.setItem(
          "codeVerifierTimestamp",
          (Date.now() + 60000).toString()
        );

        this.generateCodeChallenge(codeVerifier).then((codeChallenge) => {
          let url;
          if (location.host.includes("localhost")) {
            url =
              environment["authorization_endpoint"] +
              "?redirect_uri=http://" +
              location.host +
              "/welcome" +
              "&response_type=code&state=" +
              this.randomAlphaNumeric(40) +
              "&client_id=" +
              environment["clientId"] +
              "&scope=" +
              environment["scopes"] +
              "&code_challenge=" +
              codeChallenge +
              "&code_challenge_method=S256";
          } else {
            url =
              environment["authorization_endpoint"] +
              "?redirect_uri=https://" +
              location.host +
              "/welcome" +
              "&response_type=code&state=" +
              this.randomAlphaNumeric(40) +
              "&client_id=" +
              environment["clientId"] +
              "&scope=" +
              environment["scopes"] +
              "&code_challenge=" +
              codeChallenge +
              "&code_challenge_method=S256";
          }
          location.href = url;
        });
      }
    }
  }
  logoutIDP(): void {
    if (this.config) {
      if (environment) {
        const idToken = localStorage.getItem("idToken");
        localStorage.removeItem("codeVerifier");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("idToken");
        location.href =
          environment["end_session_endpoint"] +
          "?post_logout_redirect_uri=https://" +
          location.host +
          "/" +
          "&id_token_hint=" +
          idToken;
      }
    }
  }
  private getRedirectUri(): string {
    const protocol = location.host.includes("localhost") ? "http" : "https";
    return `${protocol}://${location.host}/welcome`;
  }

  private storeTokensAndScheduleRefresh(response: any): void {
    if (!response) return;

    localStorage.removeItem("codeVerifier");
    localStorage.removeItem("codeVerifierTimestamp");
    this.setAccessToken(response["access_token"]);
    localStorage.setItem("refreshToken", response["refresh_token"]);
    localStorage.setItem("idToken", response["id_token"]);
    localStorage.setItem("expireTime", response["expires_in"]);

    // Programar refresh automático al 80% del tiempo de expiración
    this.expiryTime = setTimeout(() => {
      if (
        localStorage.getItem("refreshToken") &&
        localStorage.getItem("refreshToken") !== "undefined"
      ) {
        this.getRefreshToken().subscribe();
      }
    }, response["expires_in"] * 800);
  }

  getToken(code: any): Observable<any> {
    // Si ya hay una petición en curso, retornar la misma
    if (this.tokenRequest$) {
      return this.tokenRequest$;
    }

    if (!environment) {
      return of(null);
    }

    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    const body = new URLSearchParams();
    body.set("grant_type", "authorization_code");
    body.set("client_id", environment["clientId"]);
    body.set("redirect_uri", this.getRedirectUri());
    body.set("code", code);
    body.set("code_verifier", localStorage.getItem("codeVerifier"));

    this.tokenRequest$ = this.http
      .post<any>(environment["token_endpoint"], body.toString(), {
        headers,
      })
      .pipe(
        tap((value) => this.storeTokensAndScheduleRefresh(value)),
        catchError(() => {
          this.snackBar.open("S'ha produït un error.", "Tancar", {
            panelClass: "error",
            duration: 5000,
          });
          localStorage.removeItem("codeVerifier");
          localStorage.removeItem("codeVerifierTimestamp");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("idToken");
          this.router.navigate(["/403"]);
          return of(null);
        }),
        share(), // Comparte la petición entre múltiples suscriptores
        finalize(() => {
          // Limpia la referencia cuando termine
          this.tokenRequest$ = null;
        })
      );

    return this.tokenRequest$;
  }

  getRefreshToken(): Observable<any> {
    // Si ya hay una petición en curso, retornar la misma
    if (this.refreshTokenRequest$) {
      return this.refreshTokenRequest$;
    }

    if (!environment) {
      return of(null);
    }

    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken || refreshToken === "undefined") {
      return of(null);
    }

    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    const body = new URLSearchParams();
    body.set("grant_type", "refresh_token");
    body.set("client_id", environment["clientId"]);
    body.set("scope", environment["scopes"]);
    body.set("refresh_token", refreshToken);

    this.refreshTokenRequest$ = this.http
      .post<IdpConfig>(environment.token_endpoint, body.toString(), {
        headers,
      })
      .pipe(
        tap((value) => this.storeTokensAndScheduleRefresh(value)),
        catchError((err) => {
          localStorage.removeItem("codeVerifier");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("idToken");
          this.router.navigate(["/403"]);
          return throwError(() => err);
        }),
        share(),
        finalize(() => {
          this.refreshTokenRequest$ = null;
        })
      );

    return this.refreshTokenRequest$;
  }
}
